/**
 * VGreen Lug Width Finder.
 *
 * Connects three Google Apps Script endpoints (brands, families, watches)
 * via a single URL with action= query param. Caches each fetch in
 * sessionStorage so repeat visits within a tab don't re-hit the script.
 *
 * Each result card has a single CTA that opens the Artisan Stylist
 * with ?lug_width=X&watch=Y in the URL. The Stylist passes the width
 * onto the destination product page where vgreen-configurator.js
 * pre-selects the matching variant option.
 *
 * Configure via data attributes on the .vgreen-lug-finder element:
 *   data-api-url       — Google Apps Script URL
 *   data-stylist-url   — path to Artisan Stylist page
 *   data-preset-brand  — (optional) skip brand selection, lock to this brand;
 *                        used on brand landing pages (e.g. /pages/seiko-watch-straps).
 *                        User can click "Switch brand" to unlock.
 */
(function () {
  'use strict';

  function init(root) {
    const apiUrl = root.dataset.apiUrl;
    if (!apiUrl) {
      console.warn('[VGreen lug-finder] Missing data-api-url');
      return;
    }
    const stylistUrl = root.dataset.stylistUrl || '/pages/artisan-stylist';
    const presetBrand = root.dataset.presetBrand || '';

    const brandSelect       = root.querySelector('[data-vgreen-lug="brand"]');
    const brandPresetEl     = root.querySelector('[data-vgreen-lug="brand-preset"]');
    const brandPresetLabel  = root.querySelector('[data-vgreen-lug="brand-preset-label"]');
    const brandSwitchBtn    = root.querySelector('[data-vgreen-lug="brand-switch"]');
    const familySelect      = root.querySelector('[data-vgreen-lug="family"]');
    const refInput          = root.querySelector('[data-vgreen-lug="ref"]');
    const resultsBox        = root.querySelector('[data-vgreen-lug="results"]');
    const resultsList       = root.querySelector('[data-vgreen-lug="results-list"]');
    const loadingBox        = root.querySelector('[data-vgreen-lug="loading"]');
    const loadingText       = root.querySelector('[data-vgreen-lug="loading-text"]');

    let currentModelWatches = [];
    let brandsLoaded = false;

    function showLoading(message) {
      loadingText.textContent = message;
      loadingBox.classList.add('is-visible');
      loadingBox.classList.remove('is-error');
    }

    function showError(message) {
      loadingText.textContent = message;
      loadingBox.classList.add('is-visible', 'is-error');
    }

    function hideLoading() {
      loadingBox.classList.remove('is-visible', 'is-error');
    }

    function populateSelect(select, options, defaultText) {
      // Build via DOM to avoid HTML injection from external API.
      while (select.firstChild) select.removeChild(select.firstChild);
      const placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = '— ' + defaultText + ' —';
      select.appendChild(placeholder);
      options.forEach((value) => {
        const opt = document.createElement('option');
        opt.value = value;
        opt.textContent = value;
        select.appendChild(opt);
      });
      select.disabled = false;
    }

    async function fetchFromAPI(url, cacheKey) {
      try {
        const cached = sessionStorage.getItem(cacheKey);
        if (cached) return JSON.parse(cached);
      } catch (e) { /* ignore quota / privacy errors */ }

      const response = await fetch(url);
      if (!response.ok) throw new Error('HTTP ' + response.status);
      const data = await response.json();
      try { sessionStorage.setItem(cacheKey, JSON.stringify(data)); }
      catch (e) { /* ignore quota errors */ }
      return data;
    }

    function buildStylistHref(watch) {
      const params = new URLSearchParams();
      params.set('lug_width', String(watch.LugWidth));
      const watchName = [brandSelect.value, watch.Name || watch.Family, watch.Ref]
        .filter(Boolean).join(' ').trim();
      if (watchName) params.set('watch', watchName);
      return stylistUrl + '?' + params.toString();
    }

    function renderResults(watches) {
      // Empty out and rebuild via DOM (no innerHTML w/ external data).
      while (resultsList.firstChild) resultsList.removeChild(resultsList.firstChild);

      if (watches.length === 0) {
        const empty = document.createElement('li');
        empty.className = 'vgreen-lug-finder__empty';
        empty.textContent = 'No watches match this reference.';
        resultsList.appendChild(empty);
        resultsBox.classList.add('is-visible');
        return;
      }

      watches.forEach((watch) => {
        const card = document.createElement('li');
        card.className = 'vgreen-lug-finder__card';

        const header = document.createElement('div');
        header.className = 'vgreen-lug-finder__card-header';

        const meta = document.createElement('div');
        const name = document.createElement('p');
        name.className = 'vgreen-lug-finder__card-name';
        name.textContent = watch.Name || watch.Family || 'Unnamed model';
        meta.appendChild(name);
        if (watch.Ref) {
          const ref = document.createElement('p');
          ref.className = 'vgreen-lug-finder__card-ref';
          ref.textContent = 'Ref: ' + watch.Ref;
          meta.appendChild(ref);
        }
        header.appendChild(meta);

        const lug = document.createElement('div');
        lug.className = 'vgreen-lug-finder__card-lug';
        lug.innerHTML = 'Lug: <span class="vgreen-lug-finder__card-lug-value"></span>';
        lug.querySelector('.vgreen-lug-finder__card-lug-value').textContent = watch.LugWidth + ' mm';
        header.appendChild(lug);

        card.appendChild(header);

        const actions = document.createElement('div');
        actions.className = 'vgreen-lug-finder__card-actions';

        // CTA → Artisan Stylist with lug width + watch name in the URL
        const stylistCta = document.createElement('a');
        stylistCta.className = 'vgreen-lug-finder__cta vgreen-lug-finder__cta--primary';
        stylistCta.href = buildStylistHref(watch);
        stylistCta.innerHTML = 'Find a strap for this watch <span class="vgreen-lug-finder__cta-arrow" aria-hidden="true">&rarr;</span>';
        actions.appendChild(stylistCta);

        card.appendChild(actions);
        resultsList.appendChild(card);
      });

      resultsBox.classList.add('is-visible');
    }

    function resetDownstream() {
      familySelect.innerHTML = '<option value="">— Select a brand first —</option>';
      familySelect.disabled = true;
      refInput.value = '';
      refInput.disabled = true;
      resultsBox.classList.remove('is-visible');
    }

    function loadFamiliesFor(brand) {
      showLoading('Finding models for ' + brand + '…');
      fetchFromAPI(
        apiUrl + '?action=getFamilies&brand=' + encodeURIComponent(brand),
        'vgreenLugModels_' + brand
      )
        .then((data) => {
          populateSelect(familySelect, data, 'Choose a model');
          hideLoading();
        })
        .catch(() => {
          showError('Could not load models. Refresh to try again.');
        });
    }

    function loadBrands() {
      if (brandsLoaded) return;
      showLoading('Loading watch brands…');
      fetchFromAPI(apiUrl + '?action=getBrands', 'vgreenLugBrands')
        .then((data) => {
          populateSelect(brandSelect, data, 'Choose a brand');
          brandsLoaded = true;
          hideLoading();
        })
        .catch(() => {
          showError('Could not load watch list. Refresh to try again.');
        });
    }

    // --- Initialization ---
    if (presetBrand) {
      // Preset: show locked brand display, skip getBrands, load families directly.
      // The select stays present (hidden) so brandSelect.value still drives the
      // family/watches/stylist URL logic — just pre-populate it with one option.
      if (brandPresetLabel) brandPresetLabel.textContent = presetBrand;
      if (brandPresetEl) brandPresetEl.hidden = false;
      if (brandSelect) {
        brandSelect.hidden = true;
        populateSelect(brandSelect, [presetBrand], 'Choose a brand');
        brandSelect.value = presetBrand;
      }
      loadFamiliesFor(presetBrand);
    } else {
      // Standalone: hide preset display, load brand list.
      if (brandPresetEl) brandPresetEl.hidden = true;
      loadBrands();
    }

    // Switch brand: unlock the dropdown, reset everything, load brand list lazily.
    if (brandSwitchBtn) {
      brandSwitchBtn.addEventListener('click', function () {
        if (brandPresetEl) brandPresetEl.hidden = true;
        if (brandSelect) {
          brandSelect.hidden = false;
          brandSelect.value = '';
        }
        resetDownstream();
        loadBrands();
      });
    }

    // 2. Brand → load families
    brandSelect.addEventListener('change', function () {
      const brand = this.value;
      familySelect.innerHTML = '<option value="">— Select a brand first —</option>';
      familySelect.disabled = true;
      refInput.value = '';
      refInput.disabled = true;
      resultsBox.classList.remove('is-visible');

      if (!brand) return;
      loadFamiliesFor(brand);
    });

    // 3. Family → load watches
    familySelect.addEventListener('change', function () {
      const family = this.value;
      refInput.value = '';
      refInput.disabled = true;
      resultsBox.classList.remove('is-visible');

      if (!family) return;
      showLoading('Looking up watches…');
      fetchFromAPI(
        apiUrl + '?action=getWatches'
          + '&brand=' + encodeURIComponent(brandSelect.value)
          + '&family=' + encodeURIComponent(family),
        'vgreenLugWatches_' + brandSelect.value + '_' + family
      )
        .then((data) => {
          currentModelWatches = data;
          refInput.disabled = false;
          refInput.placeholder = 'Filter by reference (optional)…';
          hideLoading();
          renderResults(currentModelWatches);
        })
        .catch(() => {
          showError('Could not load watches. Refresh to try again.');
        });
    });

    // 4. Reference filter
    refInput.addEventListener('input', function () {
      const val = this.value.trim().toLowerCase();
      if (!val) {
        renderResults(currentModelWatches);
        return;
      }
      const filtered = currentModelWatches.filter((w) =>
        (w.Ref || '').toLowerCase().includes(val)
      );
      renderResults(filtered);
    });
  }

  document.querySelectorAll('.vgreen-lug-finder').forEach(init);
})();
